import { ExtraPnlColumnsEnum, PnlCategoryLabelsEnum } from "types/enums";
import { PnlBarStyles } from "./PnlChartColumnsStyles";
import { PnlColumnType } from "./pnlChartColumnsTypes";
import { HoursAndMinutes } from "./subcomponents/HoursAndMinutes";

const DEFAULT_COLUMN_PROPS = {
  flexGrow: { sm: 0.7, md: 0.7 },
  minWidthsPx: { sm: 100, md: 100 },
  alignRight: true,
};

/** the first column is separate from subsequent columns:
 * it's always visible on the left, and it doesn't scroll horizontally
 * (columns 2 and above scroll horizontally, and get grouped into horizontal "pages")
 */
export const TAG_NAMES_COLUMN: PnlColumnType = {
  dataKey: "title",
  alignRight: false,
  minWidthsPx: { sm: 122, md: 296 },
  formatFn: ({ title }) => title,
  // TAG_NAMES_COLUMN is always on the left & has fixed width,
  // so the following keys do nothing
  order: 0,
  flexGrow: { sm: 1, md: 1 },
};

/** standard columns: these columns are displayed for all customers; EXTRA_PNL_COLUMNS are displayed for select customers
 *
 * each column displays one type of data from the data source pnlBreakdownChartCategorySectionsArr
 */
export const PNL_CHART_COLUMNS: PnlColumnType[] = [
  {
    ...DEFAULT_COLUMN_PROPS,
    order: 0,
    dataKey: "time",
    minWidthsPx: { sm: 80, md: 80 },
    formatFn: ({ time }) => {
      return <HoursAndMinutes {...{ timeMs: time }} />;
    },
  },
  {
    ...DEFAULT_COLUMN_PROPS,
    order: 1,
    dataKey: "percent",
    flexGrow: { sm: 0.3, md: 0.3 },
    minWidthsPx: { sm: 60, md: 60 },
    formatFn: ({ percent: normalizedPct, pnlTitle }) =>
      pnlTitle &&
      // don't show percents for notScheduled, uncollected
      [
        PnlCategoryLabelsEnum.notScheduled,
        PnlCategoryLabelsEnum.uncollected,
      ].includes(pnlTitle) ? (
        ""
      ) : normalizedPct || normalizedPct === 0 ? (
        <span
          className={(normalizedPct * 100).toFixed(0) === "0" ? "zero" : ""}
        >
          {(normalizedPct * 100).toFixed(0)}%
        </span>
      ) : (
        "--"
      ),
  },
  {
    // pnl colored bar
    order: 2,
    dataKey: "percentWidth",
    alignRight: false,
    flexGrow: { sm: 10, md: 10 },
    minWidthsPx: { sm: 140, md: 180 },
    formatFn: ({ percentWidth: normalizedPctWidth, color }) => (
      <PnlBarStyles
        {...{ $normalizedPctWidth: normalizedPctWidth, $color: color }}
      >
        {normalizedPctWidth && Math.abs(normalizedPctWidth) > 1.001 ? (
          /* javascript calculations aren't perfect, so we compare to 1.001 instead of 1 */
          <div className={`rectArrowRight`} />
        ) : null}
      </PnlBarStyles>
    ),
  },
];

export const NUMBER_OF_EVENTS_KEY = "numberOfEvents";

/** these extra columns don't appear by default & must be configured configured in [LD flag flight-extra-pnl-chart-columns](https://app.launchdarkly.com/default/production/features/flight-extra-pnl-chart-columns/targeting) */
export const EXTRA_PNL_COLUMNS: {
  [extraPnlColumnType: string]: PnlColumnType;
} = {
  /** "Freq." i.e. how many times did this tag occur */
  [ExtraPnlColumnsEnum.numberOfEvents]: {
    ...DEFAULT_COLUMN_PROPS,
    order: 1.1,
    dataKey: NUMBER_OF_EVENTS_KEY,
    columnHeaderTitle: "Freq.",
    formatFn: ({ numberOfEvents }) => {
      return numberOfEvents ? (
        <div className="numberOfEvents">{numberOfEvents.toFixed(0)}</div>
      ) : null;
    },
  },
  /** "Avg." i.e. average duration of each tag */
  [ExtraPnlColumnsEnum.averageDurationMs]: {
    ...DEFAULT_COLUMN_PROPS,
    order: 1.2,
    dataKey: "averageDurationMs",
    columnHeaderTitle: "Avg.",
    formatFn: ({ averageDurationMs }) => {
      return averageDurationMs || averageDurationMs === 0 ? (
        <HoursAndMinutes {...{ timeMs: averageDurationMs }} />
      ) : null;
    },
  },
  /** "Units" or "Cups", "Bottles"
   * (this column can be used multiple times, once per unit e.g. cups, bottles)
   *
   * (if its an allocated tag, we set outputLoss to 0. there's no way of knowing the target since we don't do it on a segment level
   *
   * so in case you see any "unexpected" 0's or blanks, that's why)
   */
  [ExtraPnlColumnsEnum.outputLoss]: {
    ...DEFAULT_COLUMN_PROPS,
    order: 1.3,
    dataKey: ExtraPnlColumnsEnum.outputLoss,
    columnHeaderTitle: "Units",
    outputLossUnit: "units",
    formatFn: ({ outputLoss }) => {
      return outputLoss ? (
        <div className={ExtraPnlColumnsEnum.outputLoss}>
          {outputLoss.toFixed(0)}
        </div>
      ) : null;
    },
  },
};
